import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { InventoryItem, Invoice, InvoiceItem } from '../types/interfaces';
import { MaterialType } from '../types/enums';
import KegScanOverlay from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import { isTaproomCustomer } from '../utils/taproomCustomer';
import { centralToday } from '../utils/centralDate';
import {
  resolveProductPackagePrice,
  listPackagedSkuOptions,
  ProductWithPackages,
} from '../utils/salesLinePrice';
import { matchKegToLine } from '../utils/kegLineMatch';
import KegTagsPanel from './KegTagsPanel';

// Added interface to extend InvoiceItem with kegCodes
interface ExtendedInvoiceItem extends InvoiceItem {
  kegCodes?: string[];
  priceProductId?: number | null;
  priceMissing?: boolean;
}

const InvoiceComponent: React.FC<{ inventory: InventoryItem[], refreshInventory: () => Promise<void> }> = ({
  inventory,
  refreshInventory,
}) => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<ExtendedInvoiceItem[]>([]); // Updated to use ExtendedInvoiceItem
  const [products, setProducts] = useState<ProductWithPackages[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Added states for keg scanning
  const [kegScanning, setKegScanning] = useState<{ itemIndex: number | null; kegCodes: string[] }>({ itemIndex: null, kegCodes: [] });
  // Latest scan list for queued camera scans (they can land before a re-render).
  const kegScanningRef = useRef(kegScanning);
  kegScanningRef.current = kegScanning;
  const [bolMeta, setBolMeta] = useState({ poNumber: '', pickupDate: '', taxType: 'Tax Paid' });
  const [savingBolMeta, setSavingBolMeta] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [qboStatus, setQboStatus] = useState<{
    sync?: { status: string; label: string; error?: string | null; qboInvoiceId?: string };
    email?: { emailed: boolean; label: string | null; emailedTo?: string; emailedAt?: string };
    qboInvoiceId?: string | null;
  } | null>(null);
  const [qboBusy, setQboBusy] = useState(false);
  const [showKegTags, setShowKegTags] = useState(false);

  const loadQboStatus = async (id: string | number) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/invoices/${id}/status`);
      if (res.status === 404) {
        setQboStatus(null);
        return;
      }
      if (!res.ok) return;
      setQboStatus(await res.json());
    } catch {
      /* sync optional */
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch invoice: HTTP ${res.status}`);
        }
        const data = await res.json();
        setInvoice(data);
        const taproom = isTaproomCustomer({
          name: data.customerName,
          customerType: data.customerType,
        });
        setBolMeta({
          poNumber: data.poNumber || data.salesOrderPoNumber || '',
          pickupDate: data.pickupDate || data.postedDate || data.createdDate || '',
          taxType: data.taxType || (taproom ? 'Tax Free' : 'Tax Paid'),
        });
        setItems(
          data.items && Array.isArray(data.items)
            ? data.items.map((item: InvoiceItem) => ({
                ...item,
                price: taproom ? '0.00' : (item.price ?? ''),
                hasKegDeposit: !!item.hasKegDeposit,
                kegCodes: item.kegCodes || [], // Added kegCodes
                priceMissing: false,
                priceProductId: null,
                kegDeposit: item.kegDeposit ? {
                  ...item.kegDeposit,
                  price: item.kegDeposit.price || '0.00',
                  hasKegDeposit: !!item.kegDeposit.hasKegDeposit,
                  isSubCharge: item.kegDeposit.isSubCharge || false,
                } : null,
              }))
            : []
        );
        if (invoiceId) void loadQboStatus(invoiceId);
      } catch (err: any) {
        setError('Failed to load invoice: ' + err.message);
      }
    };
    fetchInvoice();
  }, [invoiceId]);

  const retryQboSync = async () => {
    if (!invoiceId) return;
    setQboBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/invoices/${invoiceId}/sync`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        throw new Error(body.error || 'QuickBooks sync failed');
      }
      setSuccessMessage('Synced to QuickBooks.');
      await loadQboStatus(invoiceId);
    } catch (err: any) {
      setError(err.message || 'QuickBooks sync failed');
      if (invoiceId) await loadQboStatus(invoiceId);
    } finally {
      setQboBusy(false);
    }
  };

  const emailFromQbo = async () => {
    if (!invoiceId) return;
    setQboBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/invoices/${invoiceId}/email`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Email from QuickBooks failed');
      if (body.sent) {
        setSuccessMessage(`Emailed to ${body.emailedTo} at ${body.emailedAt}`);
      } else {
        setError(body.reason || 'Could not email from QuickBooks');
      }
      await loadQboStatus(invoiceId);
    } catch (err: any) {
      setError(err.message || 'Email from QuickBooks failed');
    } finally {
      setQboBusy(false);
    }
  };

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/products`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Invoice: failed to load products for pricing', err);
      }
    };
    void loadProducts();
  }, []);

  const addItem = () => {
    setItems([...items, { itemName: '', quantity: 0, unit: 'Units', hasKegDeposit: false, price: '', kegCodes: [], priceMissing: false, priceProductId: null }]);
  };

  const taproomInvoice = isTaproomCustomer({
    name: invoice?.customerName,
    customerType: invoice?.customerType,
  });

  const packagedSkuOptions = listPackagedSkuOptions(products);
  const inventorySkuOptions = inventory
    .filter(
      (i) =>
        (i.type === MaterialType.FinishedGoods || i.type === MaterialType.Marketing) &&
        i.identifier !== 'Keg Deposit'
    )
    .map((i) => i.identifier);
  const itemSelectOptionsFor = (current: string) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const value of [current, ...packagedSkuOptions, ...inventorySkuOptions]) {
      const v = String(value || '').trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out;
  };
  const fieldContrast = {
    backgroundColor: '#FFFFFF',
    color: '#222222',
    border: '1px solid #CCCCCC',
    borderRadius: '4px',
    fontSize: '16px',
  } as const;

  const updateItem = (index: number, field: keyof ExtendedInvoiceItem, value: any) => {
    const updatedItems = [...items];
    if (field === 'itemName' && typeof value === 'string') {
      if (taproomInvoice) {
        const resolved = resolveProductPackagePrice(value, products);
        const selectedItem = inventory.find((inv) => inv.identifier === value);
        updatedItems[index] = {
          ...updatedItems[index],
          itemName: value,
          price: '0.00',
          hasKegDeposit: resolved.hasKegDeposit || selectedItem?.isKegDepositItem === 1,
          priceMissing: false,
          priceProductId: resolved.productId,
        };
      } else {
        const resolved = resolveProductPackagePrice(value, products);
        if (resolved.price != null) {
          updatedItems[index] = {
            ...updatedItems[index],
            itemName: value,
            price: resolved.price,
            hasKegDeposit: resolved.hasKegDeposit,
            priceMissing: false,
            priceProductId: resolved.productId,
          };
        } else {
          const selectedItem = inventory.find((inv) => inv.identifier === value);
          if (selectedItem?.price != null && String(selectedItem.price).trim() !== '') {
            updatedItems[index] = {
              ...updatedItems[index],
              itemName: value,
              price: parseFloat(String(selectedItem.price)).toFixed(2),
              hasKegDeposit: selectedItem.isKegDepositItem === 1,
              priceMissing: false,
              priceProductId: resolved.productId,
            };
          } else {
            updatedItems[index] = {
              ...updatedItems[index],
              itemName: value,
              price: '',
              hasKegDeposit: false,
              priceMissing: !!value,
              priceProductId: resolved.productId,
            };
          }
        }
      }
    } else if (field === 'price') {
      updatedItems[index] = {
        ...updatedItems[index],
        price: taproomInvoice ? '0.00' : value,
        priceMissing: taproomInvoice ? false : String(value || '').trim() === '',
      };
    } else if (field === 'hasKegDeposit') {
      updatedItems[index] = { ...updatedItems[index], hasKegDeposit: value };
      if (!value) {
        updatedItems[index].kegCodes = []; // Clear kegCodes if hasKegDeposit is unchecked
      }
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
    }
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSaveBolMeta = async () => {
    if (!invoiceId || savingBolMeta) return;
    setError(null);
    try {
      setSavingBolMeta(true);
      const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          poNumber: bolMeta.poNumber.trim() || null,
          pickupDate: bolMeta.pickupDate.trim() || null,
          taxType: bolMeta.taxType.trim() || 'Tax Paid',
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInvoice((prev) => (prev ? { ...prev, ...data } : data));
      setSuccessMessage('BOL fields saved');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to save BOL fields: ' + err.message);
    } finally {
      setSavingBolMeta(false);
    }
  };

  /**
   * Check one keg for invoice line `index` and add it to the scan list.
   * Throws (with the message to show) when the keg can't go on this line.
   */
  const addInvoiceKeg = async (index: number, raw: string): Promise<string> => {
    const code = normalizeKegCode(raw);
    if (!isValidKegCode(code)) throw new Error('Valid keg tag needed (e.g., EK09503)');
    const current = kegScanningRef.current;
    if (current.kegCodes.map((c) => normalizeKegCode(c)).includes(code)) {
      throw new Error(`Keg ${code} already added`);
    }
    const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Keg not found: HTTP ${res.status}, ${text}`);
    }
    const keg = await res.json();
    if (String(keg.status || '').toLowerCase() !== 'filled') {
      throw new Error(`Keg ${code} is not filled (status: ${keg.status})`);
    }
    const item = items[index];
    const resolved = resolveProductPackagePrice(item.itemName, products);
    const match = matchKegToLine(
      {
        code,
        productId: keg.productId,
        productName: keg.productName,
        packagingType: keg.packagingType,
        packageType: keg.packageType,
      },
      {
        itemName: item.itemName,
        productId: resolved.productId ?? item.priceProductId ?? null,
        productName: resolved.productName,
        packageType: resolved.packageType,
      },
      { kegCode: code }
    );
    if (!match.ok) {
      throw new Error(match.message);
    }
    const latest = kegScanningRef.current;
    if (latest.kegCodes.map((c) => normalizeKegCode(c)).includes(code)) {
      throw new Error(`Keg ${code} already added`);
    }
    const next = { ...latest, kegCodes: [...latest.kegCodes, code] };
    kegScanningRef.current = next;
    setKegScanning(next);
    setError(null);
    return code;
  };

  // Added function to save scanned keg codes
  const saveKegCodes = () => {
    if (kegScanning.itemIndex === null) return;
    const updatedItems = [...items];
    updatedItems[kegScanning.itemIndex].kegCodes = kegScanning.kegCodes;
    setItems(updatedItems);
    setKegScanning({ itemIndex: null, kegCodes: [] });
    setSuccessMessage('Keg codes saved');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleSave = async () => {
    // Draft save may omit keg serials; posting enforces reconcile keg assets.
    try {
      console.log('handleSave: Saving invoice', { invoiceId, items });
      const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map(({ id, itemName, quantity, unit, price, hasKegDeposit, kegCodes }) => ({
            id,
            itemName,
            quantity,
            unit,
            price,
            hasKegDeposit,
            kegCodes, // Added kegCodes
          })),
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to save invoice: ${text}`);
      }
      const updatedInvoice = await res.json();
      console.log('handleSave: Invoice saved successfully', updatedInvoice);
      setInvoice(updatedInvoice);
      setItems(
        updatedInvoice.items && Array.isArray(updatedInvoice.items)
          ? updatedInvoice.items.map((item: InvoiceItem) => ({
              ...item,
              price: item.price || '0.00',
              hasKegDeposit: !!item.hasKegDeposit,
              kegCodes: item.kegCodes || [], // Added kegCodes
              kegDeposit: item.kegDeposit ? {
                ...item.kegDeposit,
                price: item.kegDeposit.price || '0.00',
                hasKegDeposit: !!item.kegDeposit.hasKegDeposit,
                isSubCharge: item.kegDeposit.isSubCharge || false,
              } : null,
            }))
          : []
      );
      setSuccessMessage('Invoice saved successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('handleSave: Error', { invoiceId, error: errorMessage });
      setError('Failed to save invoice: ' + errorMessage);
    }
  };

  const handlePost = async () => {
    for (const item of items) {
      if (item.hasKegDeposit && (!item.kegCodes || item.kegCodes.length !== item.quantity)) {
        setError(
          `Reconcile keg assets: ${item.itemName} requires exactly ${item.quantity} keg serials before shipping/invoicing`
        );
        return;
      }
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}/post`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          message = await res.text();
        }
        throw new Error(message);
      }
      const data = await res.json();
      await refreshInventory();
      setInvoice((prev) =>
        prev
          ? {
              ...prev,
              status: 'Posted',
              postedDate: centralToday(),
              total: data.total,
              subtotal: data.subtotal,
              keg_deposit_total: data.keg_deposit_total,
            }
          : null
      );
      setItems(
        data.items && Array.isArray(data.items)
          ? data.items.map((item: InvoiceItem) => ({
              ...item,
              price: item.price || '0.00',
              hasKegDeposit: !!item.hasKegDeposit,
              kegCodes: item.kegCodes || [], // Added kegCodes
              kegDeposit: item.kegDeposit ? {
                ...item.kegDeposit,
                price: item.kegDeposit.price || '0.00',
                hasKegDeposit: !!item.kegDeposit.hasKegDeposit,
                isSubCharge: item.kegDeposit.isSubCharge || false,
              } : null,
            }))
          : items
      );
      setSuccessMessage('Invoice posted successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to post invoice: ' + err.message);
    }
  };

  const handleResendAssociateEmail = async () => {
    if (!invoiceId || notifying) return;
    setNotifying(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}/notify`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      let data: { ok?: boolean; to?: string; error?: string; message?: string } = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON */
      }
      if (!res.ok || !data.ok) {
        setError(data.error || data.message || `Failed to send email (HTTP ${res.status})`);
        return;
      }
      setSuccessMessage(data.message || `Email sent to ${data.to}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError('Failed to send email: ' + (err?.message || 'Unknown error'));
    } finally {
      setNotifying(false);
    }
  };

  if (!invoice) return <div>Loading...</div>;

  return (
    <div className="page-container">
      <h2 style={{ color: '#EEC930', fontSize: '24px', marginBottom: '20px' }}>
        Invoice {invoice.invoiceNumber || invoice.invoiceId}
      </h2>
      {error && <div className="error">{error}</div>}
      {successMessage && (
        <div className="error" style={{ color: '#28A745', backgroundColor: '#e6ffe6' }}>
          {successMessage}
        </div>
      )}
      <div style={{ marginBottom: '20px' }}>
        {invoice.invoiceNumber && (
          <p><strong>Invoice #:</strong> {invoice.invoiceNumber}</p>
        )}
        {invoice.siteId && <p><strong>Site:</strong> {invoice.siteId}</p>}
        <p><strong>Customer:</strong> {invoice.customerName}</p>
        {(invoice.customerEmail || invoice.liveCustomerEmail) && (
          <p><strong>Customer email:</strong> {invoice.customerEmail || invoice.liveCustomerEmail}</p>
        )}
        {invoice.customerType && <p><strong>Customer type:</strong> {invoice.customerType}</p>}
        {isTaproomCustomer({ name: invoice.customerName, customerType: invoice.customerType }) && (
          <p style={{ color: '#555', fontSize: '13px' }}>
            Internal taproom invoice ($0 / Tax Free). Posting moves brewery cooler → taproom cooler
            with a TO number; keg serials required for kegs. Excluded from distributor sales reports.
          </p>
        )}
        {invoice.toNumber && <p><strong>TO Number:</strong> {invoice.toNumber}</p>}
        <p><strong>PO Number:</strong> {bolMeta.poNumber || invoice.poNumber || invoice.salesOrderPoNumber || '—'}</p>
        <p><strong>Tax Type:</strong> {bolMeta.taxType || invoice.taxType || '—'}</p>
        <p><strong>Status:</strong> {invoice.status}</p>
        <p><strong>Created Date:</strong> {invoice.createdDate}</p>
        {invoice.postedDate && <p><strong>Posted Date:</strong> {invoice.postedDate}</p>}
        {invoice.total && <p><strong>Total:</strong> ${parseFloat(invoice.total).toFixed(2)}</p>}
        {qboStatus && (
          <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #444' }}>
            <p>
              <strong>QuickBooks:</strong>{' '}
              {qboStatus.sync?.status === 'synced' && qboStatus.qboInvoiceId
                ? `Synced (QuickBooks #${qboStatus.qboInvoiceId})`
                : qboStatus.sync?.label || 'Not synced'}
            </p>
            {qboStatus.sync?.error && (
              <p style={{ color: '#c0392b', fontSize: 14 }}>{qboStatus.sync.error}</p>
            )}
            {qboStatus.email?.label && <p style={{ fontSize: 14 }}>{qboStatus.email.label}</p>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {(qboStatus.sync?.status === 'failed' ||
                qboStatus.sync?.status === 'not_synced' ||
                qboStatus.sync?.status === 'pending') && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  style={{ minHeight: 44 }}
                  disabled={qboBusy}
                  onClick={() => void retryQboSync()}
                >
                  Retry QuickBooks sync
                </button>
              )}
              {qboStatus.sync?.status === 'synced' && qboStatus.qboInvoiceId && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  style={{ minHeight: 44 }}
                  disabled={qboBusy}
                  onClick={() => void emailFromQbo()}
                >
                  Email from QuickBooks
                </button>
              )}
              <Link to="/settings/quickbooks" style={{ alignSelf: 'center' }}>
                Settings › QuickBooks
              </Link>
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          marginBottom: '20px',
          padding: '12px',
          border: '1px solid #444',
          borderRadius: '4px',
          maxWidth: '640px',
        }}
      >
        <h3 style={{ color: '#EEC930', marginBottom: '10px', fontSize: '18px' }}>Bill of Lading</h3>
        <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>PO Number</span>
            <input
              type="text"
              value={bolMeta.poNumber}
              onChange={(e) => setBolMeta({ ...bolMeta, poNumber: e.target.value })}
              style={{ padding: '8px', border: '1px solid #CCCCCC', borderRadius: '4px' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Delivery / Pickup Date</span>
            <input
              type="date"
              value={bolMeta.pickupDate || ''}
              onChange={(e) => setBolMeta({ ...bolMeta, pickupDate: e.target.value })}
              style={{ padding: '8px', border: '1px solid #CCCCCC', borderRadius: '4px' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Tax Type</span>
            <select
              value={bolMeta.taxType}
              onChange={(e) => setBolMeta({ ...bolMeta, taxType: e.target.value })}
              style={{ padding: '8px', border: '1px solid #CCCCCC', borderRadius: '4px' }}
            >
              <option value="Tax Paid">Tax Paid</option>
              <option value="Tax Free">Tax Free</option>
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleSaveBolMeta}
            disabled={savingBolMeta}
            style={{
              backgroundColor: '#2196F3',
              color: '#fff',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {savingBolMeta ? 'Saving…' : 'Save BOL Fields'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/invoices/${invoiceId}/bol`)}
            style={{
              backgroundColor: '#1a5f2a',
              color: '#fff',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Print BOL
          </button>
          <button
            type="button"
            onClick={() => setShowKegTags(true)}
            data-testid="invoice-keg-tags"
            style={{
              backgroundColor: '#374151',
              color: '#fff',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Keg Tags
          </button>
        </div>
      </div>
      <h3 style={{ color: '#EEC930', marginBottom: '10px' }}>Items</h3>
      {invoice.status === 'Draft' ? (
        <>
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Price</th>
                <th>Subtotal</th>
                <th>Keg Deposit</th>
                <th>Keg Codes</th> {/* Added column for keg codes */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <select
                        value={item.itemName || ''}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateItem(index, 'itemName', e.target.value)}
                        style={{ width: 'min(100%, 240px)', padding: '10px', ...fieldContrast }}
                        disabled={kegScanning.itemIndex === index}
                        aria-label={`Invoice line ${index + 1} item`}
                        data-testid={`invoice-line-item-${index}`}
                      >
                        <option value="">Select Item</option>
                        {itemSelectOptionsFor(item.itemName || '').map((sku) => (
                          <option key={sku} value={sku}>
                            {sku}
                          </option>
                        ))}
                      </select>
                      {!!item.itemName && (
                        <div
                          style={{ color: '#222', fontSize: '12px', marginTop: '4px', maxWidth: 240 }}
                          data-testid={`invoice-line-item-label-${index}`}
                        >
                          {item.itemName}
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.quantity || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                        placeholder="Quantity"
                        min="0"
                        style={{ width: '100px', padding: '10px', ...fieldContrast }}
                        disabled={kegScanning.itemIndex === index}
                      />
                    </td>
                    <td>
                      <select
                        value={item.unit}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateItem(index, 'unit', e.target.value)}
                        style={{ width: '100px', padding: '10px', ...fieldContrast }}
                        disabled={kegScanning.itemIndex === index}
                      >
                        <option value="Units">Units</option>
                        <option value="Kegs">Kegs</option>
                        <option value="Bottles">Bottles</option>
                        <option value="Cans">Cans</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.price === '' || item.price == null ? '' : item.price}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'price', e.target.value)}
                        placeholder="Price"
                        step="0.01"
                        min="0"
                        style={{
                          width: '100px',
                          padding: '10px',
                          ...fieldContrast,
                          border: item.priceMissing ? '1px solid #F86752' : fieldContrast.border,
                        }}
                        disabled={taproomInvoice || kegScanning.itemIndex === index}
                        title={taproomInvoice ? 'Taproom invoices are $0' : undefined}
                      />
                    </td>
                    <td>
                      $
                      {(
                        (parseFloat(String(item.price || '0')) || 0) * (item.quantity || 0)
                      ).toFixed(2)}
                    </td>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={item.hasKegDeposit}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'hasKegDeposit', e.target.checked)}
                          disabled={kegScanning.itemIndex === index}
                        />
                        Keg Deposit
                      </label>
                    </td>
                    <td>
                      {item.hasKegDeposit && (
                        <button
                          onClick={() => {
                            primeScanAudio(); // iOS: sound needs this tap
                            setKegScanning({ itemIndex: index, kegCodes: item.kegCodes || [] });
                          }}
                          style={{
                            backgroundColor: '#2196F3',
                            color: '#fff',
                            padding: '8px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          disabled={kegScanning.itemIndex !== null}
                        >
                          {item.kegCodes && item.kegCodes.length > 0 ? `Edit Kegs (${item.kegCodes.length})` : 'Scan Kegs'}
                        </button>
                      )}
                      {item.kegCodes && item.kegCodes.length > 0 && (
                        <p style={{ marginTop: '5px' }}>{item.kegCodes.join(', ')}</p>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => removeItem(index)}
                        style={{
                          backgroundColor: '#F86752',
                          color: '#fff',
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        disabled={kegScanning.itemIndex === index}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  {!taproomInvoice && item.priceMissing && item.itemName && (
                    <tr>
                      <td colSpan={8} style={{ color: '#B45309', fontSize: '13px', paddingTop: 0 }}>
                        No packaging price found for this item.
                        {item.priceProductId ? (
                          <>
                            {' '}
                            <Link to={`/products/${item.priceProductId}`}>Open product pricing</Link>
                            {' '}to set it, or type a price here.
                          </>
                        ) : (
                          <> Enter a price, or add the SKU under Product → Package Types.</>
                        )}
                      </td>
                    </tr>
                  )}
                  {item.kegDeposit && (
                    <tr>
                      <td style={{ paddingLeft: '30px' }}>Keg Deposit (Sub-Charge)</td>
                      <td>{item.kegDeposit.quantity}</td>
                      <td>{item.kegDeposit.unit}</td>
                      <td>${parseFloat(item.kegDeposit.price).toFixed(2)}</td>
                      <td>${(parseFloat(item.kegDeposit.price) * item.kegDeposit.quantity).toFixed(2)}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <button
            onClick={addItem}
            style={{
              backgroundColor: '#2196F3',
              color: '#fff',
              padding: '8px 12px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
            disabled={kegScanning.itemIndex !== null}
          >
            Add Item
          </button>
        </>
      ) : (
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Price</th>
              <th>Subtotal</th>
              <th>Keg Codes</th> {/* Added column for keg codes */}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <React.Fragment key={item.id || `${item.itemName}-${item.quantity}`}>
                <tr>
                  <td style={{ color: '#222' }} data-testid="invoice-readonly-item-name">
                    {item.itemName || 'Unknown'}
                  </td>
                  <td>{item.quantity}</td>
                  <td>{item.unit}</td>
                  <td>${parseFloat(item.price).toFixed(2)}</td>
                  <td>${(parseFloat(item.price) * item.quantity).toFixed(2)}</td>
                  <td>{item.kegCodes && item.kegCodes.length > 0 ? item.kegCodes.join(', ') : '-'}</td>
                </tr>
                {item.kegDeposit && (
                  <tr>
                    <td style={{ paddingLeft: '30px' }}>Keg Deposit (Sub-Charge)</td>
                    <td>{item.kegDeposit.quantity}</td>
                    <td>{item.kegDeposit.unit}</td>
                    <td>${parseFloat(item.kegDeposit.price).toFixed(2)}</td>
                    <td>${(parseFloat(item.kegDeposit.price) * item.kegDeposit.quantity).toFixed(2)}</td>
                    <td>-</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      {invoice.status !== 'Draft' && (
        <div style={{ marginTop: '20px' }}>
          <p><strong>Subtotal:</strong> ${invoice.subtotal ? parseFloat(invoice.subtotal).toFixed(2) : '0.00'}</p>
          <p><strong>Keg Deposit Total:</strong> ${invoice.keg_deposit_total ? parseFloat(invoice.keg_deposit_total).toFixed(2) : '0.00'}</p>
          <p><strong>Invoice Total:</strong> ${invoice.total ? parseFloat(invoice.total).toFixed(2) : '0.00'}</p>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginTop: '20px',
        }}
      >
        {invoice.status === 'Draft' && (
          <>
            <button
              onClick={handleSave}
              style={{
                backgroundColor: '#2196F3',
                color: '#fff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                minHeight: '44px',
                fontSize: '16px',
                fontWeight: 600,
              }}
              disabled={kegScanning.itemIndex !== null || notifying}
            >
              Save Invoice
            </button>
            <button
              onClick={handlePost}
              style={{
                backgroundColor: '#28A745',
                color: '#fff',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                minHeight: '44px',
                fontSize: '16px',
                fontWeight: 600,
              }}
              disabled={kegScanning.itemIndex !== null || notifying}
            >
              Post Invoice
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handleResendAssociateEmail}
          data-testid="invoice-resend-email"
          style={{
            backgroundColor: '#0F766E',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: notifying || kegScanning.itemIndex !== null ? 'wait' : 'pointer',
            opacity: notifying ? 0.7 : 1,
            minHeight: '44px',
            fontSize: '16px',
            fontWeight: 600,
          }}
          disabled={notifying || kegScanning.itemIndex !== null}
          aria-label="Resend sales associate email"
        >
          {notifying ? 'Sending…' : 'Resend Email'}
        </button>
        <button
          onClick={() => navigate('/invoices')}
          style={{
            backgroundColor: '#F86752',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            minHeight: '44px',
            fontSize: '16px',
            fontWeight: 600,
          }}
          disabled={kegScanning.itemIndex !== null || notifying}
        >
          Cancel
        </button>
      </div>
      {kegScanning.itemIndex !== null && items[kegScanning.itemIndex] && (
        <KegScanOverlay
          title="Scan Kegs"
          subtitle={items[kegScanning.itemIndex].itemName}
          codes={kegScanning.kegCodes}
          target={Number(items[kegScanning.itemIndex].quantity) > 0 ? Number(items[kegScanning.itemIndex].quantity) : undefined}
          onScan={async (code) => {
            await addInvoiceKeg(kegScanning.itemIndex!, code);
          }}
          onRemove={(c) => {
            const latest = kegScanningRef.current;
            const drop = normalizeKegCode(c);
            const next = {
              ...latest,
              kegCodes: latest.kegCodes.filter((x) => normalizeKegCode(x) !== drop),
            };
            kegScanningRef.current = next;
            setKegScanning(next);
          }}
          doneLabel="Save"
          onDone={saveKegCodes}
          onCancel={() => {
            setKegScanning({ itemIndex: null, kegCodes: [] });
            setError(null);
          }}
        />
      )}
      {showKegTags && invoiceId && (
        <KegTagsPanel
          apiPath={`invoices/${invoiceId}/keg-tags`}
          parentLabel="Invoices"
          parentTo="/invoices"
          documentLabel={
            invoice.invoiceNumber
              ? `Invoice ${invoice.invoiceNumber}`
              : `Invoice ${invoiceId}`
          }
          onClose={() => setShowKegTags(false)}
        />
      )}
    </div>
  );
};

export default InvoiceComponent;